import { isInsertKnowledgeSlashCommand } from "@app/components/editor/extensions/shared/SlashCommandCapabilitiesItems";
import { isPickModelSlashCommand } from "@app/components/editor/extensions/shared/slash_suggestion/pickModelSlashCommand";
import type { SlashCommand } from "@app/components/editor/extensions/shared/slash_suggestion/SlashCommandDropdown";
import type { Editor, Range } from "@tiptap/core";

export const ATTACH_CONTEXT_SUB_MENU_ID = "attach-context";
export const PICK_MODEL_SUB_MENU_ID = "pick-model";

export type SlashSubMenuId =
  | typeof ATTACH_CONTEXT_SUB_MENU_ID
  | typeof PICK_MODEL_SUB_MENU_ID;

export interface SlashMenuStackFrame {
  command: SlashCommand;
  subMenuId: SlashSubMenuId;
}

interface SlashMenuNavigationStorage {
  menuStack: SlashMenuStackFrame[];
}

interface SlashCommandEditorStorage extends SlashMenuNavigationStorage {
  hasBeenFocused: boolean;
}

interface InputBarSlashSuggestionEditorStorage
  extends SlashMenuNavigationStorage {
  dismissedTriggerStart: number | null;
  hasBeenFocused: boolean;
}

declare module "@tiptap/core" {
  interface Storage {
    inputBarSlashSuggestion: InputBarSlashSuggestionEditorStorage;
    slashCommand: SlashCommandEditorStorage;
  }
}

export function createSlashMenuNavigationStorage(): SlashMenuNavigationStorage {
  return {
    menuStack: [],
  };
}

function getSlashCommandSubMenuId(item: SlashCommand): SlashSubMenuId | null {
  if (isInsertKnowledgeSlashCommand(item)) {
    return ATTACH_CONTEXT_SUB_MENU_ID;
  }

  if (isPickModelSlashCommand(item)) {
    return PICK_MODEL_SUB_MENU_ID;
  }

  return null;
}

// Number of leading query words that spell the label from `start`: the first word prefixes the
// label word at `start`, each following word equals the next label word ("pick model gpt6").
// Anything shorter stays part of the query, so "pick m" keeps "m" for the medium effort.
function countLabelWords(
  queryWords: string[],
  labelWords: string[],
  start: number
): number {
  let count = 1;
  while (
    count < queryWords.length &&
    start + count < labelWords.length &&
    queryWords[count] === labelWords[start + count]
  ) {
    count++;
  }

  return count;
}

/**
 * @cc [owner:PopDaph,label:product] space-enters-first-sub-menu-command
 * A query containing a space resolves to the sub-menu of the first item of `commandItems` whose
 * label has a word (labels split on whitespace and hyphens) starting, case-insensitively, with
 * the text before the first space. Following query words belong to the command while they equal
 * the next label words exactly; the remaining text is the sub-menu query. It resolves to `null`
 * when the first text is empty, no label matches, or the first match is not a sub-menu command.
 */
export function resolveSlashSubMenuFromQuery({
  commandItems,
  query,
}: {
  commandItems: SlashCommand[];
  query: string;
}): { frame: SlashMenuStackFrame; query: string } | null {
  if (query.indexOf(" ") <= 0) {
    return null;
  }

  const rawWords = query.split(" ");
  const queryWords = rawWords.map((word) => word.toLowerCase());
  for (const command of commandItems) {
    const labelWords = command.label.toLowerCase().split(/[\s-]+/);
    const start = labelWords.findIndex((word) =>
      word.startsWith(queryWords[0])
    );
    if (start === -1) {
      continue;
    }

    const subMenuId = getSlashCommandSubMenuId(command);
    if (!subMenuId) {
      return null;
    }

    const consumed = countLabelWords(queryWords, labelWords, start);
    return {
      frame: { command, subMenuId },
      query: rawWords.slice(consumed).join(" "),
    };
  }

  return null;
}

export function getActiveSlashSubMenuFrame(
  storage: SlashMenuNavigationStorage
): SlashMenuStackFrame | null {
  return storage.menuStack[storage.menuStack.length - 1] ?? null;
}

export function enterSlashSubMenu({
  command,
  editor,
  range,
  storage,
  subMenuId,
}: {
  command: SlashCommand;
  editor: Editor;
  range: Range;
  storage: SlashMenuNavigationStorage;
  subMenuId: SlashSubMenuId;
}) {
  storage.menuStack = [...storage.menuStack, { command, subMenuId }];

  const chain = editor.chain().focus();
  if (range.to > range.from + 1) {
    chain.deleteRange({ from: range.from + 1, to: range.to });
  }
  chain.run();

  // Entering a sub-menu mutates extension storage only. Force a transaction so
  // dropdown hooks subscribed to editor updates re-render (e.g. click with empty query).
  editor.view.dispatch(editor.state.tr.setMeta("slashMenuNavigation", true));
}

export function popSlashSubMenu({
  editor,
  range,
  storage,
}: {
  editor: Editor;
  range: Range;
  storage: SlashMenuNavigationStorage;
}) {
  storage.menuStack = storage.menuStack.slice(0, -1);

  const chain = editor.chain().focus();
  if (range.to > range.from + 1) {
    chain.deleteRange({ from: range.from + 1, to: range.to });
  }
  chain.run();

  editor.view.dispatch(editor.state.tr.setMeta("slashMenuNavigation", true));
}

export function clearSlashSubMenuStack(storage: SlashMenuNavigationStorage) {
  storage.menuStack = [];
}

export function handleSlashSubMenuCommand({
  command,
  editor,
  range,
  storage,
}: {
  command: SlashCommand;
  editor: Editor;
  range: Range;
  storage: SlashMenuNavigationStorage;
}): boolean {
  const subMenuId = getSlashCommandSubMenuId(command);
  if (!subMenuId) {
    return false;
  }

  enterSlashSubMenu({
    command,
    editor,
    range,
    storage,
    subMenuId,
  });
  return true;
}
